import * as React from "react";
import { Button } from "@krix/ui";
import { useLocale } from "~/lib/locale-provider.tsx";

const STORAGE_KEY = "krix.consent";

type Consent = {
  necessary: true;
  analytics: boolean;
  decidedAt: string;
};

export function CookieBanner() {
  const { t } = useLocale();
  const [decision, setDecision] = React.useState<Consent | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Consent) : null;
    } catch {
      return null;
    }
  });

  const save = (analytics: boolean) => {
    const next: Consent = {
      necessary: true,
      analytics,
      decidedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setDecision(next);
    if (analytics) initPostHog();
  };

  React.useEffect(() => {
    if (decision?.analytics) initPostHog();
  }, [decision?.analytics]);

  if (decision) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-2 bottom-2 z-50 mx-auto max-w-2xl rounded-xl border border-[var(--color-border)] bg-[var(--color-popover)] p-4 shadow-[var(--shadow-lg)] md:p-6"
    >
      <h2 className="text-base font-semibold">{t("cookies.title")}</h2>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{t("cookies.body")}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={() => save(true)} size="sm">
          {t("cookies.accept")}
        </Button>
        <Button onClick={() => save(false)} size="sm" variant="outline">
          {t("cookies.reject")}
        </Button>
      </div>
    </div>
  );
}

let posthogInitialized = false;
function initPostHog() {
  if (posthogInitialized) return;
  const key = import.meta.env["VITE_POSTHOG_KEY"];
  const host = import.meta.env["VITE_POSTHOG_HOST"] ?? "https://eu.i.posthog.com";
  if (!key) return;
  posthogInitialized = true;
  // Lazy-load the SDK only after consent. Avoids the bundle hit by default.
  import("posthog-js")
    .then((ph) => {
      ph.default.init(key, {
        api_host: host,
        capture_pageview: true,
        autocapture: false,
        disable_session_recording: true,
        persistence: "localStorage",
      });
    })
    .catch(() => {
      posthogInitialized = false;
    });
}
